// コメントスレッドのポップオーバー。
// - スレッド内の全メッセージ（投稿者・時刻・本文）を時系列で表示する。
// - 返信用の入力欄を備える（ENTER で送信 / SHIFT+ENTER で改行）。
// - 投稿者本人または管理者は、各メッセージの編集・削除ができる（最終判定はサーバ）。
// 解決機能・未読既読は持たない（要望どおり）。
//
// ビューポート内に収まるよう、測定後に表示位置をクランプする。

import {
  CloseIconData,
  DeleteIconData,
  EditIconData,
  MoreHorizIconData,
} from "@meiden-connect/material-symbols";
import { Icon, useShowConfirmDialog } from "@meiden-connect/ui";
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import {
  canDeleteCommentThread,
  markdownTableAvailableEm,
  type CommentMessage,
  type CommentThreadCanvasObject,
} from "@refinement-canvas/domain";
import type { CanvasOpDispatcher } from "../use-canvas-op-dispatcher";
import { CommentAvatar } from "./comment-avatar";
import { CommentComposer } from "./comment-composer";
import { CommentMessageBody } from "./comment-message-body";
import type { CommentImageUploader } from "./comment-image-upload";
import { confirmDeleteCommentThread } from "./comment-thread-delete";
import { formatCommentAbsolute, formatCommentTimestamp } from "./comment-time";

interface Props {
  readonly thread: CommentThreadCanvasObject;
  /** アンカー（ピンの左下）の screen 座標。 */
  readonly screenX: number;
  readonly screenY: number;
  readonly currentUserId: string;
  readonly canAdmin: boolean;
  /** 参照専用（埋め込み）。true でメッセージ閲覧のみ許可し、返信 / 編集 / 削除を非表示にする。 */
  readonly readOnly?: boolean | undefined;
  readonly dispatcher: CanvasOpDispatcher;
  readonly onClose: () => void;
  /** 返信への画像貼り付けを有効にする場合のアップローダ（参照専用等では未指定）。 */
  readonly imageUploader?: CommentImageUploader | undefined;
  /** 返信の送信確定処理中かどうかの変化を親へ通知する（送信中はポップオーバーを閉じないため）。 */
  readonly onSubmittingChange?: ((submitting: boolean) => void) | undefined;
}

const POPOVER_WIDTH = 320;
const VIEWPORT_MARGIN = 8;
/** メッセージ行の左右の内側余白（MessageRow の `padding: "8px 12px"`）。 */
const MESSAGE_ROW_PADDING_X_PX = 12;
/** 本文の左に並ぶ投稿者アバターの幅（CommentAvatar の size）と、本文との間隔（flex gap）。 */
const MESSAGE_AVATAR_SIZE_PX = 28;
const MESSAGE_AVATAR_GAP_PX = 8;
/** コメント本文の文字サイズ（px）。本文コンテナの fontSize と表の使用可能幅の基準に使う。 */
const MESSAGE_BODY_FONT_PX = 14;
/**
 * コメント本文の領域幅（px）。ポップオーバーの固定幅から行の内側余白・アバター・間隔を引いた値で、
 * DOM の実測に依らず表の使用可能幅（em）を決めるために使う。
 */
const MESSAGE_BODY_WIDTH_PX =
  POPOVER_WIDTH - MESSAGE_ROW_PADDING_X_PX * 2 - MESSAGE_AVATAR_SIZE_PX - MESSAGE_AVATAR_GAP_PX;
/** コメント本文の表の使用可能幅（em）。固定幅・固定文字サイズのため定数になる。 */
const MESSAGE_BODY_TABLE_AVAILABLE_EM = markdownTableAvailableEm(
  MESSAGE_BODY_WIDTH_PX,
  MESSAGE_BODY_FONT_PX
);

export function CommentThreadPopover({
  thread,
  screenX,
  screenY,
  currentUserId,
  canAdmin,
  readOnly = false,
  dispatcher,
  onClose,
  imageUploader,
  onSubmittingChange,
}: Props): JSX.Element {
  const cardRef = useRef<HTMLDivElement>(null);
  const showConfirmDialog = useShowConfirmDialog();
  // 返信の送信確定処理中は、閉じる導線（ヘッダーの ✕）を無効化する。外側クリック / Escape での
  // クローズ抑止は、状態を親（comment-layer）へ伝えてそちらで行う（openThreadId を握るのが親のため）。
  const [replySubmitting, setReplySubmitting] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number }>({
    left: screenX,
    top: screenY,
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  // クリックで原寸表示する添付画像（ライトボックス）。null は非表示。
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  // スレッド削除の権限（クライアント UI とサーバで同じ domain 関数を正本にする）。
  // 参照専用（埋め込み）では削除導線を出さない。
  const canDeleteThread = !readOnly && canDeleteCommentThread(thread, currentUserId, canAdmin);

  // ヘッダーのゴミ箱ボタンからスレッド全体を削除する。確認ダイアログ後に削除し、ポップオーバーを閉じる。
  // 削除は楽観反映で、スレッドが消えると CommentLayer の効果でポップオーバーは自動的に閉じる
  // （ここでの onClose は即時性のため）。利用者が「スレッド削除」を明示確認した上での操作のため、
  // 返信欄の未送信テキストはこの時点で破棄される想定（最後のメッセージ削除でスレッドごと閉じるのと同じ挙動）。
  // 稀にサーバが reject（競合で他者の返信が先に入り単独投稿者でなくなる等）した場合はスレッドが復元されるが、
  // 既に閉じているため再度ピンをクリックして開く必要がある（楽観削除＋確認済み意図の一貫した挙動）。
  const handleDeleteThread = async (): Promise<void> => {
    if (!(await confirmDeleteCommentThread(showConfirmDialog))) return;
    dispatcher.deleteObject(thread.id);
    onClose();
  };

  // 測定して画面内へ収める。アンカー（ピン左下）の少し上に開き、はみ出す場合はクランプする。
  useLayoutEffect(() => {
    const el = cardRef.current;
    if (typeof window === "undefined") return;
    const width = el?.offsetWidth ?? POPOVER_WIDTH;
    const height = el?.offsetHeight ?? 0;
    const left = clamp(screenX, VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN);
    // ピンの上側に出すのを基本とし、上に入りきらなければ下側へ。
    const above = screenY - height - 12;
    const below = screenY + 12;
    const top =
      above >= VIEWPORT_MARGIN
        ? above
        : clamp(below, VIEWPORT_MARGIN, window.innerHeight - height - VIEWPORT_MARGIN);
    setPos({ left, top: Math.max(VIEWPORT_MARGIN, top) });
  }, [screenX, screenY, thread.messages.length, editingId]);

  const cardStyle: CSSProperties = {
    position: "fixed",
    left: `${pos.left}px`,
    top: `${pos.top}px`,
    width: `${POPOVER_WIDTH}px`,
    maxHeight: `${Math.max(200, (typeof window === "undefined" ? 800 : window.innerHeight) - 2 * VIEWPORT_MARGIN)}px`,
    background: "var(--color-surface, #ffffff)",
    borderRadius: "12px",
    boxShadow: "0 10px 32px rgba(0,0,0,0.26)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    pointerEvents: "auto",
    zIndex: 1,
  };

  return (
    <div
      ref={cardRef}
      style={cardStyle}
      data-testid={`comment-thread-popover-${thread.id}`}
      data-comment-ui=""
      role="dialog"
      aria-label="コメント"
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      {/* ヘッダー（タイトル + 閉じる）。解決機能は持たない。 */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 12px",
          borderBottom: "1px solid var(--color-border, #ececf1)",
        }}
      >
        <span style={{ fontWeight: 700, fontSize: "13px", color: "var(--color-text, #1a1a2e)" }}>
          コメント
        </span>
        {/* 操作ボタン群。削除（ゴミ箱）は権限があるときのみ表示し、閉じる（✕）の左隣に置く。
            返信の送信確定処理中は、スレッド削除（＝送信中スレッドの消失→返信 no-op と昇格済み
            画像の孤児化を招く）と閉じる（✕）をどちらも無効化する。 */}
        <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
          {canDeleteThread ? (
            <IconButton
              label="スレッドを削除"
              iconData={DeleteIconData}
              onClick={() => void handleDeleteThread()}
              danger
              disabled={replySubmitting}
            />
          ) : null}
          <IconButton
            label="閉じる"
            iconData={CloseIconData}
            onClick={onClose}
            disabled={replySubmitting}
          />
        </div>
      </div>

      {/* メッセージ一覧 */}
      <div style={{ overflowY: "auto", padding: "4px 0", flex: "1 1 auto" }}>
        {thread.messages.map((message) => (
          <MessageRow
            key={message.id}
            message={message}
            isOwn={message.authorId === currentUserId}
            // 返信の送信確定処理中は、メッセージの編集・削除メニューも抑止する。全メッセージ削除は
            // スレッドごと消えて送信中の返信が no-op になり、昇格済み画像が孤児化するため。
            canEdit={
              !readOnly && !replySubmitting && (message.authorId === currentUserId || canAdmin)
            }
            isEditing={editingId === message.id}
            onStartEdit={() => setEditingId(message.id)}
            onCancelEdit={() => setEditingId(null)}
            onSaveEdit={(body) => {
              dispatcher.updateCommentMessage(thread.id, message.id, body);
              setEditingId(null);
            }}
            onDelete={() => dispatcher.deleteCommentMessage(thread.id, message.id)}
            onOpenImage={(url) => setLightboxUrl(url)}
          />
        ))}
      </div>

      {/* 返信入力（参照専用では非表示） */}
      {readOnly ? null : (
        <div
          style={{
            borderTop: "1px solid var(--color-border, #ececf1)",
            padding: "8px 12px",
          }}
        >
          <CommentComposer
            placeholder="返信"
            // ポップオーバーを開いた直後に返信欄へフォーカスし、一度クリックしなくてもすぐ入力できるようにする。
            autoFocus
            onSubmit={(body, images) => dispatcher.addCommentMessage(thread.id, body, images)}
            clearOnSubmit
            imageUploader={imageUploader}
            onSubmittingChange={(submitting) => {
              setReplySubmitting(submitting);
              onSubmittingChange?.(submitting);
            }}
          />
        </div>
      )}

      {/* 添付画像のライトボックス（クリックした画像をオリジナルサイズでモーダル表示する） */}
      {lightboxUrl ? (
        <CommentImageLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
      ) : null}
    </div>
  );
}

function MessageRow({
  message,
  isOwn,
  canEdit,
  isEditing,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
  onOpenImage,
}: {
  readonly message: CommentMessage;
  readonly isOwn: boolean;
  readonly canEdit: boolean;
  readonly isEditing: boolean;
  readonly onStartEdit: () => void;
  readonly onCancelEdit: () => void;
  readonly onSaveEdit: (body: string) => void;
  readonly onDelete: () => void;
  /** 添付画像クリックでライトボックスを開く。 */
  readonly onOpenImage: (url: string) => void;
}): JSX.Element {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuAnchorRef = useRef<HTMLDivElement | null>(null);
  const showConfirmDialog = useShowConfirmDialog();

  const handleDelete = async (): Promise<void> => {
    setMenuOpen(false);
    const answer = await showConfirmDialog(
      "warning",
      "このコメントを削除しますか？",
      [
        { content: "削除する", value: "delete" },
        { content: "キャンセル", value: "cancel" },
      ],
      { ariaLabel: "コメント削除の確認" }
    );
    if (answer === "delete") onDelete();
  };

  return (
    <div style={{ padding: `8px ${MESSAGE_ROW_PADDING_X_PX}px` }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: `${MESSAGE_AVATAR_GAP_PX}px` }}>
        <CommentAvatar
          name={message.authorName}
          src={message.authorAvatar}
          size={MESSAGE_AVATAR_SIZE_PX}
          isSelf={isOwn}
        />
        <div style={{ flex: "1 1 auto", minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
            <span
              style={{
                fontWeight: 700,
                fontSize: "13px",
                color: "var(--color-text, #1a1a2e)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "140px",
              }}
            >
              {message.authorName || "利用者"}
            </span>
            <span
              style={{ fontSize: "11px", color: "var(--color-text-muted, #6b6f7d)" }}
              title={formatCommentAbsolute(message.createdAt)}
            >
              {formatCommentTimestamp(message.createdAt)}
            </span>
            {message.editedAt ? (
              <span style={{ fontSize: "11px", color: "var(--color-text-muted, #9b9fae)" }}>
                （編集済み）
              </span>
            ) : null}
            {canEdit && !isEditing ? (
              <div ref={menuAnchorRef} style={{ marginLeft: "auto", position: "relative" }}>
                <IconButton
                  label="コメントの操作"
                  iconData={MoreHorizIconData}
                  onClick={() => setMenuOpen((open) => !open)}
                />
                {menuOpen ? (
                  <MessageMenu
                    anchorRef={menuAnchorRef}
                    onEdit={() => {
                      setMenuOpen(false);
                      onStartEdit();
                    }}
                    onDelete={() => void handleDelete()}
                    onClose={() => setMenuOpen(false)}
                  />
                ) : null}
              </div>
            ) : null}
          </div>

          {isEditing ? (
            <div style={{ marginTop: "4px" }}>
              <CommentComposer
                placeholder="コメントを編集"
                initialValue={message.body}
                autoFocus
                clearOnSubmit={false}
                onSubmit={onSaveEdit}
                onCancel={onCancelEdit}
              />
              <button
                type="button"
                onClick={onCancelEdit}
                onPointerDown={(event) => event.stopPropagation()}
                style={{
                  marginTop: "2px",
                  background: "none",
                  border: "none",
                  color: "var(--color-text-muted, #6b6f7d)",
                  fontSize: "12px",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                キャンセル
              </button>
            </div>
          ) : (
            <>
              {/* 本文（空＝画像だけのメッセージのときは描画しない）。付箋・テキストと同じ
                  Markdown 記法（見出し・箇条書き）で整形表示し、URL は自動リンク化する。 */}
              {message.body.length > 0 ? (
                <div
                  style={{
                    marginTop: "2px",
                    fontSize: `${MESSAGE_BODY_FONT_PX}px`,
                    lineHeight: 1.5,
                    color: "var(--color-text, #1a1a2e)",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  <CommentMessageBody
                    text={message.body}
                    availableEm={MESSAGE_BODY_TABLE_AVAILABLE_EM}
                  />
                </div>
              ) : null}
              {/* 添付画像のサムネイル。クリックでオリジナルサイズのモーダル表示。 */}
              {message.images && message.images.length > 0 ? (
                <div style={{ marginTop: "6px", display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {message.images.map((url, index) => (
                    <button
                      key={`${index}-${url}`}
                      type="button"
                      aria-label="添付画像を拡大表示"
                      title="クリックで拡大表示"
                      onClick={() => onOpenImage(url)}
                      onPointerDown={(event) => event.stopPropagation()}
                      style={{
                        padding: 0,
                        border: "1px solid var(--color-border, #ececf1)",
                        borderRadius: "8px",
                        overflow: "hidden",
                        cursor: "zoom-in",
                        background: "none",
                        width: "132px",
                        height: "88px",
                      }}
                    >
                      <img
                        src={url}
                        alt="コメントの添付画像"
                        loading="lazy"
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      />
                    </button>
                  ))}
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// コメント本文の URL 自動リンク化（LinkifiedCommentText）は、Markdown 整形表示
// （CommentMessageBody）との共用のため comment-message-body.tsx へ移設した。

// 添付画像のライトボックス（FigJam の画像モーダルに合わせたフルスクリーン表示）。
// - 背景クリック・Escape・× ボタンで閉じる。
// - 画像はオリジナルサイズを基本とし、ビューポートに収まらない場合のみ縮小して全体を表示する。
// - ポップオーバーの外側クリック判定（comment-layer）に巻き込まれないよう data-comment-ui を付け、
//   document.body へ portal してポップオーバーの overflow にクリップされないようにする。
function CommentImageLightbox({
  url,
  onClose,
}: {
  readonly url: string;
  readonly onClose: () => void;
}): JSX.Element {
  // ヘッダーへ表示するファイル名（URL 末尾のパスセグメント。クエリ等は除く）。
  const fileName = (() => {
    try {
      const pathname = new URL(url, window.location.href).pathname;
      const last = pathname.split("/").filter((part) => part.length > 0).pop();
      return last ?? "添付画像";
    } catch {
      return "添付画像";
    }
  })();

  // Escape で閉じる（ポップオーバー自体を閉じるグローバルの Escape 処理より先に capture で消費する）。
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      onClose();
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [onClose]);

  return createPortal(
    <div
      role="dialog"
      aria-label={`添付画像の拡大表示: ${fileName}`}
      data-comment-ui=""
      data-testid="comment-image-lightbox"
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        // 画像・ヘッダー以外（背景）のクリックで閉じる。
        if (event.target === event.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(10, 10, 18, 0.82)",
        zIndex: 1000,
        display: "grid",
        placeItems: "center",
      }}
    >
      {/* ヘッダー（ファイル名 + 閉じる） */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 14px",
          color: "#ffffff",
          background: "linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0))",
          pointerEvents: "none",
        }}
      >
        <span
          style={{
            fontSize: "13px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "70vw",
          }}
        >
          {fileName}
        </span>
        <button
          type="button"
          aria-label="拡大表示を閉じる"
          title="閉じる（Esc）"
          onClick={onClose}
          style={{
            pointerEvents: "auto",
            display: "grid",
            placeItems: "center",
            width: "32px",
            height: "32px",
            border: "none",
            borderRadius: "8px",
            background: "rgba(255,255,255,0.14)",
            color: "#ffffff",
            cursor: "pointer",
          }}
        >
          <Icon d={CloseIconData} />
        </button>
      </div>
      <img
        src={url}
        alt={`コメントの添付画像（${fileName}）`}
        // オリジナルサイズ表示を基本とし、ビューポート超過時のみ全体が見えるよう縮小する。
        style={{
          maxWidth: "92vw",
          maxHeight: "86vh",
          objectFit: "contain",
          borderRadius: "4px",
          boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
        }}
      />
    </div>,
    document.body
  );
}

// メッセージごとの操作メニュー（編集 / 削除）。外側クリックで閉じる。
// ポップオーバーは overflow:hidden / overflowY:auto の入れ子になっており、メニューを子要素として
// 描画するとクリップされて隠れてしまう（特に最後のメッセージ）。これを避けるため、メニューは
// document.body へ portal し、トリガ（…ボタン）の矩形を基準に position:fixed で配置する。
const MENU_WIDTH = 132;
const MENU_VIEWPORT_MARGIN = 8;

function MessageMenu({
  anchorRef,
  onEdit,
  onDelete,
  onClose,
}: {
  readonly anchorRef: RefObject<HTMLElement | null>;
  readonly onEdit: () => void;
  readonly onDelete: () => void;
  readonly onClose: () => void;
}): JSX.Element | null {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  // トリガの矩形からメニュー位置を求める。右端揃えを基本とし、下に入りきらなければ上側へ反転する。
  // メニューは document.body へ portal するため、ポップオーバー内のスクロールやウィンドウリサイズで
  // トリガが動いてもメニューは追従しない。スクロール（capture でポップオーバー内部の overflow も拾う）
  // とリサイズで再計算し、アンカーから乖離しないようにする。
  useLayoutEffect(() => {
    if (typeof window === "undefined") return undefined;
    const updatePos = (): void => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const menuHeight = ref.current?.offsetHeight ?? 0;
      const left = clamp(
        rect.right - MENU_WIDTH,
        MENU_VIEWPORT_MARGIN,
        window.innerWidth - MENU_WIDTH - MENU_VIEWPORT_MARGIN
      );
      const below = rect.bottom + 2;
      const above = rect.top - menuHeight - 2;
      const fitsBelow = below + menuHeight <= window.innerHeight - MENU_VIEWPORT_MARGIN;
      const top = fitsBelow ? below : Math.max(MENU_VIEWPORT_MARGIN, above);
      setPos({ left, top });
    };
    updatePos();
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [anchorRef]);

  useEffect(() => {
    const onDocPointerDown = (event: PointerEvent): void => {
      const target = event.target as Node;
      // メニュー本体、またはトリガ（…ボタン）上のクリックは「外側」として扱わない。
      if (ref.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener("pointerdown", onDocPointerDown, true);
    return () => document.removeEventListener("pointerdown", onDocPointerDown, true);
  }, [anchorRef, onClose]);

  return createPortal(
    <div
      ref={ref}
      role="menu"
      // ポップオーバーの外側クリック判定（comment-layer）に閉じられないよう data-comment-ui を付ける。
      data-comment-ui=""
      style={{
        position: "fixed",
        left: `${pos?.left ?? 0}px`,
        top: `${pos?.top ?? 0}px`,
        // 位置確定前（measure 前）は不可視にして、左上からの瞬間表示によるちらつきを防ぐ。
        visibility: pos ? "visible" : "hidden",
        background: "var(--color-surface, #ffffff)",
        borderRadius: "8px",
        boxShadow: "0 6px 20px rgba(0,0,0,0.22)",
        padding: "4px",
        zIndex: 40,
        width: `${MENU_WIDTH}px`,
      }}
    >
      <MenuItem label="編集" iconData={EditIconData} onClick={onEdit} />
      <MenuItem label="削除" iconData={DeleteIconData} onClick={onDelete} danger />
    </div>,
    document.body
  );
}

function MenuItem({
  label,
  iconData,
  onClick,
  danger,
}: {
  readonly label: string;
  readonly iconData: string;
  readonly onClick: () => void;
  readonly danger?: boolean | undefined;
}): JSX.Element {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      onPointerDown={(event) => event.stopPropagation()}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        width: "100%",
        padding: "6px 8px",
        border: "none",
        background: "none",
        cursor: "pointer",
        borderRadius: "6px",
        fontSize: "13px",
        color: danger ? "var(--color-danger, #d22b2b)" : "var(--color-text, #1a1a2e)",
        textAlign: "left",
      }}
    >
      <Icon d={iconData} />
      <span>{label}</span>
    </button>
  );
}

function IconButton({
  label,
  iconData,
  onClick,
  danger,
  disabled,
}: {
  readonly label: string;
  readonly iconData: string;
  readonly onClick: () => void;
  readonly danger?: boolean | undefined;
  readonly disabled?: boolean | undefined;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      onPointerDown={(event) => event.stopPropagation()}
      style={{
        display: "grid",
        placeItems: "center",
        width: "26px",
        height: "26px",
        border: "none",
        background: "none",
        cursor: disabled ? "default" : "pointer",
        borderRadius: "6px",
        opacity: disabled ? 0.5 : 1,
        color: danger ? "var(--color-danger, #d22b2b)" : "var(--color-text-muted, #6b6f7d)",
      }}
    >
      <Icon d={iconData} />
    </button>
  );
}

function clamp(value: number, min: number, max: number): number {
  if (max < min) return min;
  if (value < min) return min;
  if (value > max) return max;
  return value;
}
